// Studio state shared by panels; settings persist in localStorage.

import { DEFAULT_DEPTH, type DepthSettings } from './depth/ops';
import { DEFAULT_TEXT, type TextParams } from './depth/text3d';
import {
  DEFAULT_STEREO,
  defaultTexture,
  type DepthData,
  type RgbaData,
  type StereoParams,
  type TextureSpec,
} from './types';

export type SourceKind = 'text' | 'image' | 'depth' | 'scene';
export type HeuristicKind = 'ml' | 'brightness' | 'edges';

export interface StudioSettings {
  sourceKind: SourceKind;
  text: TextParams;
  heuristic: HeuristicKind;
  edgeThreshold: number;
  depth: DepthSettings;
  texture: TextureSpec;
  stereo: StereoParams;
  output: { width: number; height: number; maxSide: number };
  /** Depth model used when ML runs on the home server. */
  serverModel: string;
}

const STORAGE_KEY = 'stereo.studio.v1';

export function defaultSettings(): StudioSettings {
  return {
    sourceKind: 'text',
    text: { ...DEFAULT_TEXT },
    heuristic: 'ml',
    edgeThreshold: 0.25,
    depth: { ...DEFAULT_DEPTH },
    texture: defaultTexture('random_dots'),
    stereo: { ...DEFAULT_STEREO },
    output: { width: 1280, height: 720, maxSide: 1920 },
    serverModel: 'depth-anything-v2-large',
  };
}

function load(): StudioSettings {
  const d = defaultSettings();
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return d;
    const s = JSON.parse(raw) as Partial<StudioSettings>;
    return {
      ...d,
      ...s,
      text: { ...d.text, ...s.text },
      depth: { ...d.depth, ...s.depth },
      stereo: { ...d.stereo, ...s.stereo },
      output: { ...d.output, ...s.output },
      texture: s.texture ?? d.texture,
    };
  } catch {
    return d;
  }
}

export const studio = $state<StudioSettings>(load());

/** Large binary inputs; not persisted. Raw state: never proxied, so safe to post to workers. */
class Assets {
  sourceImage = $state.raw<RgbaData | null>(null);
  /** Original upload, sent as-is to the server. */
  sourceFile = $state.raw<File | null>(null);
  sourceName = $state('');
  uploadedDepth = $state.raw<DepthData | null>(null);
  textureImage = $state.raw<RgbaData | null>(null);
  customFonts = $state.raw<{ id: string; name: string }[]>([]);
}

export const assets = new Assets();

let saveTimer: ReturnType<typeof setTimeout> | undefined;
export function persist() {
  clearTimeout(saveTimer);
  saveTimer = setTimeout(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify($state.snapshot(studio)));
    } catch {
      /* quota or disabled storage */
    }
  }, 300);
}

export function resetStudio() {
  Object.assign(studio, defaultSettings());
}
